import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import classNames from 'classnames';

import { useEventCache } from 'hooks/useLRQ/useEventCache';
import {
  initialLogRequestsState,
  logResultsTimelineStatus,
  useProgressiveStateManagement,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { getRandomNumber } from 'common/functions';
import LogResultsTimeline, { LogResultsTimelineProps } from './LogResultsTimeline';

import './LogResultsTimelineContainer.scss';

type LogResultsTimelineContainerProps = {
  noTimelineLegend?: boolean;
};

const defaultColumnSeriesData: Pick<LogResultsTimelineProps, 'columnSeriesData'>[] = [];
const defaultlineSeriesData: Pick<LogResultsTimelineProps, 'lineSeriesData'>[] = [];

/**
 * This component acts as a placeholder while the data is being loaded
 */
function LogResultsTimelineSkeleton(): JSX.Element {
  const legend = (
    <div className="LogResultsTimelineContainer__Skeleton__Legend">
      <div style={{ height: 20, width: 184 }} className="LogResultsTimelineContainer__Skeleton__Item" />
      <div style={{ height: 20, width: 108 }} className="LogResultsTimelineContainer__Skeleton__Item" />
    </div>
  );
  const timelineData = useMemo(() => [...Array(50)].map(() => getRandomNumber(20, 90)), []);
  const timeline = (
    <div className="LogResultsTimelineContainer__Skeleton__Timeline">
      <div className="LogResultsTimelineContainer__Skeleton__Timeline__Plots">
        {timelineData.map((barHeight, index) => (
        // eslint-disable-next-line react/no-array-index-key
          <div key={index} style={{ height: `${barHeight}%` }} className="LogResultsTimelineContainer__Skeleton__Timeline__Plot" />
        ))}
      </div>
    </div>
  );

  return (
    <div className="LogResultsTimelineContainer--Legend LogResultsTimelineContainer__Skeleton skeleton-opacity-animation">
      {legend}
      {timeline}
    </div>
  );
}

/**
 *  A container wrapper to manage timeline data states and timeline component.
 *
 * @prop noTimelineLegend    if true, do not show a legend above the timeline
 */
function LogResultsTimelineContainer({
  noTimelineLegend = false,
}: LogResultsTimelineContainerProps): JSX.Element | null {

  const initialLogRequests = useRecoilValue(initialLogRequestsState);

  const columnDataId = initialLogRequests?.[0]?.queriesVariables?.[1]?.id || '';
  const lineDataId = initialLogRequests?.[0]?.queriesVariables?.[2]?.id || '';
  const eventCacheIds = [ columnDataId, lineDataId ];
  let eventCache = useEventCache(eventCacheIds);
  const isLoading = (eventCache.results?.length > 0) ? (!eventCache.results.every((result) => result.isDone)) : true;
  const returnPreviousCache = (eventCache.results?.[0]?.data == null && eventCache.results?.[1]?.data == null) || false;

  eventCache = useProgressiveStateManagement(eventCache, eventCacheIds, logResultsTimelineStatus, returnPreviousCache);
  const { results: [ { seriesData: columnSeriesData, plotData: columnPlotData }, { seriesData: lineSeriesData } ] }  = eventCache;
  const isPreviousDataAvailable = columnSeriesData && lineSeriesData && columnSeriesData.matchCount > 0; // to know when to show skeleton UI

  const className = classNames('LogResultsTimelineContainer', {
    'LogResultsTimelineContainer--Legend': !noTimelineLegend,
    'LogResultsTimelineContainer--NoTimelineLegend': noTimelineLegend,
    'dimming-overlay-effect': returnPreviousCache,
  });
  let view: JSX.Element | null = (
    <div className={className}>
      <LogResultsTimeline
        columnSeriesData={columnSeriesData?.series || defaultColumnSeriesData}
        columnSliceInfo={columnPlotData?.sliceInfo}
        lineSeriesData={lineSeriesData?.series || defaultlineSeriesData}
        matchCount={columnSeriesData?.matchCount}
        noTimelineLegend={noTimelineLegend}
      />
    </div>
  );

  if (isLoading && !isPreviousDataAvailable) {
    view = <LogResultsTimelineSkeleton />;
  }

  if (!isLoading && (!columnSeriesData || !lineSeriesData || columnSeriesData.matchCount === 0)) {
    view = null;
  }

  return view;
}

export default LogResultsTimelineContainer;
